<template>
  <v-container fluid class="hyp-table-container">
    <v-row>
      <v-col cols="12" class="details-heading application-heading pull-left">
        <span>Setup Profile </span>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        :style="{ 'max-height': listingHeight + 'px' }"
        class="overflow-y-auto"
      >
        <v-row>
          <v-col cols="12" class="details-heading">{{ labelType }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pb-0">
            <span v-html="assetTypesDescription"></span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0">
            <v-list>
              <template v-for="(systemAssetType, i) in systemAssetTypes">
                <v-divider
                  v-if="!systemAssetType"
                  :key="`divider-${i}`"
                ></v-divider>

                <AssetsCustomTypeEditor
                  :key="`type-system-${i}`"
                  :asset-type="systemAssetType"
                  @edit="handleClickEditType"
                  @save="handleClickSaveType"
                  @cancel="handleClickCancelType"
                ></AssetsCustomTypeEditor>
              </template>
            </v-list>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="details-heading pb-0">
            <span v-html="assetTypesCustomDescription"></span>
          </v-col>
        </v-row>
        <v-row v-if="customAssetTypes && customAssetTypes.length">
          <v-col class="pt-0 pb-0">
            <Loader v-if="loading" text="" color="black" class="mt-3" />
            <v-list v-if="!loading">
              <template v-for="(customAssetType, i) in customAssetTypes">
                <v-divider
                  v-if="!customAssetType"
                  :key="`divider-${i}`"
                ></v-divider>

                <AssetsCustomTypeEditor
                  :key="`type-custom-${i}`"
                  :asset-type="customAssetType"
                  :title-editable="true"
                  @edit="handleClickEditType"
                  @save="handleClickSaveType"
                  @cancel="handleClickCancelType"
                ></AssetsCustomTypeEditor>
              </template>
            </v-list>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-btn small class="btn primary" @click="handleClickAddCustomType"
              >Add Custom Type</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
      <v-col
        :style="{ 'max-height': listingHeight + 'px' }"
        class="overflow-y-auto"
      >
        <v-row>
          <v-col cols="12" class="details-heading pb-0">Custom Fields</v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 pb-0">
            <Loader v-if="loading" text="" color="black" class="mt-3" />
            <v-list v-if="!loading">
              <template v-for="(field, i) in customAssetFields">
                <AssetsCustomFieldEditor
                  :key="`field-custom-${i}`"
                  :asset-field="field"
                  @edit="handleClickEditField"
                  @save="handleClickSaveField"
                  @cancel="handleClickCancelField"
                ></AssetsCustomFieldEditor>
              </template>
            </v-list>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-btn small class="btn primary" @click="handleClickAddCustomField"
              >Add Custom Field</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashSortBy from "lodash/sortBy";

import { mapGetters } from "vuex";
import { LABELS } from "@/common/constants/labels";
import { MODULE_PREFIX_ASSETS } from "@/store";

import {
  DEFAULT_ASSET_TYPES,
  ASSET_TYPES_DESCRIPTION,
  ASSET_TYPES_CUSTOM_DESCRIPTION,
} from "@/common/constants/assetManagement";
import { GET_ASSET_CUSTOM_FIELDS, GET_ASSET_TYPES } from "@/store/actions.type";

export default {
  name: "AssetsSetup",
  components: {
    Loader: () => import("@/components/common/Loader"),
    AssetsCustomTypeEditor: () =>
      import("@/components/assetManagement/AssetsCustomTypeEditor"),
    AssetsCustomFieldEditor: () =>
      import("@/components/assetManagement/AssetsCustomFieldEditor"),
  },
  data() {
    return {
      defaultList: DEFAULT_ASSET_TYPES,
      assetTypesDescription: ASSET_TYPES_DESCRIPTION,
      assetTypesCustomDescription: ASSET_TYPES_CUSTOM_DESCRIPTION,
      labelType: `${LABELS.asset} Types`,
      systemAssetTypes: [],
      customAssetTypes: [],
      customAssetFields: [],
      defaultIcon: "star",
      defaultColor: "#000000",
      windowSize: {
        x: 0,
        y: 0,
      },
      loading: false,
    };
  },
  async mounted() {
    this.loading = true;
    await this.getAssetTypes();
    this.setupAssetTypes();
    await this.getCustomFields();
    this.setupCustomFields();
    this.handleResize();
    this.loading = false;
  },
  computed: {
    ...mapGetters({
      assetTypes: MODULE_PREFIX_ASSETS + "assetTypes",
      customFields: MODULE_PREFIX_ASSETS + "assetCustomFields",
    }),
    listingHeight() {
      return this.windowSize.y - 250;
    },
  },
  methods: {
    async getAssetTypes() {
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSET_TYPES, {});
    },
    async getCustomFields() {
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSET_CUSTOM_FIELDS,
        {}
      );
    },
    setupAssetTypes() {
      //Setup System Asset Types
      this.systemAssetTypes = this.assetTypes.filter((assetType) => {
        return assetType.isSystemType;
      });
      this.defaultList.forEach((assetType) => {
        let exists = this.systemAssetTypes.some((sa) => {
          return sa.title === assetType.title;
        });

        if (!exists) {
          assetType.enabled = false;
          assetType.isDraft = true;
          this.systemAssetTypes.push(assetType);
        }
      });
      lodashSortBy(this.systemAssetTypes, ["orderBy"]); //TODO: Revisit logic

      // Setup Custom Asset Types
      this.customAssetTypes = this.assetTypes.filter((assetType) => {
        return !assetType.isSystemType;
      });
    },
    setupCustomFields() {
      this.customAssetFields = [];
      this.customAssetFields.push(...this.customFields);
    },
    handleResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    handleClickAddCustomType() {
      this.$set(this.customAssetTypes, this.customAssetTypes.length, {
        id: null,
        title: null,
        icon: this.defaultIcon,
        color: this.defaultColor,
        enabled: false,
        isSystemType: false,
        isDraft: true,
        isEditing: true,
      });
    },
    handleClickSaveType() {
      this.clearEditing();
    },
    handleClickEditType(assetType) {
      this.clearEditing();
      this.$set(assetType, "isEditing", true);
    },
    handleClickCancelType(assetType) {
      if (!assetType.id) {
        const index = this.customAssetTypes.indexOf(assetType);
        this.$delete(this.customAssetTypes, index);
      }
      this.clearEditing();
    },
    handleClickAddCustomField() {
      this.$set(this.customAssetFields, this.customAssetFields.length, {
        title: null,
        enabled: false,
        id: null,
        isDraft: true,
        isEditing: true,
      });
    },
    handleClickSaveField() {
      this.clearEditing();
    },
    handleClickEditField(assetField) {
      this.clearEditing();
      this.$set(assetField, "isEditing", true);
    },
    handleClickCancelField(assetField) {
      if (!assetField.id) {
        const index = this.customAssetFields.indexOf(assetField);
        this.$delete(this.customAssetFields, index);
      }
      this.clearEditing();
    },
    clearEditing() {
      this.systemAssetTypes.forEach((at) => {
        this.$set(at, "isEditing", false);
      });
      this.customAssetTypes.forEach((at) => {
        this.$set(at, "isEditing", false);
      });
      this.customAssetFields.forEach((af) => {
        this.$set(af, "isEditing", false);
      });
    },
  },
  watch: {
    customFields: {
      handler() {
        this.setupCustomFields();
      },
    },
    assetTypes: {
      handler() {
        this.setupAssetTypes();
      },
    },
  },
};
</script>

<style scoped>
.hyp-table-container {
  width: 1200px;
}
.details-heading {
  font-size: 18px;
  font-weight: bold;
}
.section-heading {
  font-size: 18px;
}
.content-container {
  max-width: 550px !important;
}
.color-holder {
  height: 24px;
  width: 24px;
  border-radius: 12px;
}
</style>
